import { Link, useParams, Navigate } from "react-router-dom";
import { Reveal, Stagger, StaggerItem } from "../components/Motion";
import { blogPosts } from "../lib/data";
import { ArrowRight, Clock, ArrowLeft } from "lucide-react";

export function BlogIndex() {
  const [hero, ...rest] = blogPosts;
  return (
    <div data-testid="page-blog">
      <section className="pt-32 md:pt-40 pb-16">
        <div className="container-luxe">
          <Reveal>
            <div className="overline mb-6">Блог</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight max-w-4xl">
              Замітки лікаря
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-warmGray text-lg max-w-2xl leading-relaxed">
              Чесні, експертні статті про красу, anti-age медицину, wellness терапію та сучасні
              протоколи зниження ваги.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-16">
        <div className="container-luxe">
          <Reveal>
            <Link to={`/blog/${hero.slug}`} data-testid={`blog-hero-${hero.slug}`} className="group block">
              <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                <div className="lg:col-span-7 rounded-3xl overflow-hidden">
                  <img loading="lazy" decoding="async" src={hero.cover}
                    onError={(e) => hero.fallback && (e.currentTarget.src = hero.fallback)}
                    alt={hero.title}
                    className="w-full h-[420px] md:h-[520px] object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                  />
                </div>
                <div className="lg:col-span-5">
                  <div className="overline mb-3 text-warmGray">Свіже · {hero.date}</div>
                  <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">{hero.title}</h2>
                  <p className="mt-5 text-warmGray leading-relaxed">{hero.excerpt}</p>
                  <div className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase border-b border-ink pb-1">
                    Читати <ArrowRight size={14} strokeWidth={1.5} />
                  </div>
                </div>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container-luxe">
          <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {rest.map((p) => (
              <StaggerItem key={p.slug}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group block rounded-3xl overflow-hidden border border-line bg-bone hover:border-nudeDark transition"
                >
                  <div className="overflow-hidden">
                    <img loading="lazy" decoding="async" src={p.cover}
                      onError={(e) => p.fallback && (e.currentTarget.src = p.fallback)}
                      alt={p.title}
                      className="w-full h-64 object-cover transition-transform duration-[1500ms] group-hover:scale-[1.05]"
                    />
                  </div>
                  <div className="p-7">
                    <div className="flex items-center gap-3 text-xs text-warmGray">
                      <span>{p.date}</span>
                      <span>·</span>
                      <Clock size={12} strokeWidth={1.5} />
                      <span>{p.readTime}</span>
                    </div>
                    <h3 className="font-serif text-2xl md:text-3xl mt-3 leading-tight">{p.title}</h3>
                    <p className="mt-3 text-sm text-warmGray">{p.excerpt}</p>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;
  return (
    <div data-testid={`page-blog-${slug}`}>
      <section className="pt-28 md:pt-32 pb-10">
        <div className="container-luxe">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-warmGray hover:text-ink" data-testid="back-to-blog">
            <ArrowLeft size={14} strokeWidth={1.5} /> Усі статті
          </Link>
        </div>
      </section>
      <section className="pb-12">
        <div className="container-luxe max-w-3xl">
          <Reveal>
            <div className="overline text-warmGray mb-4">{post.date} · {post.readTime}</div>
            <h1 className="font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight">{post.title}</h1>
          </Reveal>
        </div>
        <div className="container-luxe mt-12">
          <Reveal>
            <img loading="lazy" decoding="async" src={post.cover}
              onError={(e) => post.fallback && (e.currentTarget.src = post.fallback)}
              alt={post.title}
              className="w-full h-[480px] object-cover rounded-3xl"
            />
          </Reveal>
        </div>
        <div className="container-luxe max-w-3xl mt-12 space-y-6 text-lg leading-relaxed text-ink/85">
          <Reveal><p>{post.excerpt}</p></Reveal>
          <Reveal>
            <p>
              Цей матеріал — короткий тизер. Повна стаття зʼявиться найближчим часом. Якщо у Вас
              є питання саме зараз — натисніть «Онлайн запис» і ми обговоримо все на консультації.
            </p>
          </Reveal>
          <Reveal>
            <p>
              Я регулярно публікую розбори протоколів, відповіді на питання пацієнтів та клінічні
              кейси. Слідкуйте за оновленнями у Telegram та Instagram.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
